import { FeatureCode, Program } from '@playbook-technologies/common-enums';
import {
  BundleFreeTrial,
  SubscriptionBundlePrice,
  SubscriptionBundle,
  Features,
  RawSubscriptionBundle,
  RawFeatures,
  RawPrograms,
  RawSubscriptionTier,
} from '../../common/bundle.types';
import { UpdateBundleInput, BundleItemType } from 'generated/graphql';

export const toSubscriptionPrograms = (programs?: RawPrograms) =>
  (programs || [])
    .filter((p) => p.accessType === Program.AccessType.Premium.value)
    .map((p) => ({
      id: p.id,
      name: p.name || '',
      isPublished: !!p.publishedAt && !p.archivedAt,
    }));

export const toSubscriptionBundles = (
  bundles: RawSubscriptionBundle[],
): SubscriptionBundle[] =>
  bundles
    .map((b) => ({
      id: b.id,
      title: b.isPlatformBundle ? 'Platform users' : 'Exclusive Access',
      influencerId: b.influencerId,
      items: (b?.items || []).map((i) => ({
        contentId: i.contentId,
        itemId: i.itemId,
        itemType: i.itemType as BundleItemType,
      })),
      isPlatformBundle: b.isPlatformBundle,
      freeTrial: getFreeTrialFromSubscriptionTier(b.subscriptionTier),
      price: getPricesFromSubscriptionTier(b.subscriptionTier),
      pricing: b.pricing,
      status: b.status,
      description: b.description,
    }))
    .sort(function (a, b) {
      return b.id - a.id;
    });

export const toFeatures = (features: RawFeatures): Features =>
  features.map((f) => ({
    id: f.id,
    name: f.name,
    code: FeatureCode[f.code],
  }));

const getPricesFromSubscriptionTier = (
  subscriptionTier: RawSubscriptionTier,
): SubscriptionBundlePrice => {
  const monthlyPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === 'Month',
  );
  const annualPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === 'Year',
  );

  return {
    monthly: monthlyPrice?.price ? monthlyPrice.price / 100 : undefined,
    annual: annualPrice?.price ? annualPrice.price / 100 : undefined,
  };
};

const getFreeTrialFromSubscriptionTier = (
  subscriptionTier: RawSubscriptionTier,
): BundleFreeTrial => {
  const monthlyPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === 'Month',
  );
  const annualPrice = subscriptionTier?.prices?.find(
    (p) => p.billingCycleInterval === 'Year',
  );

  return {
    monthly: !!monthlyPrice?.isFreeTrialEnabled,
    annual: !!annualPrice?.isFreeTrialEnabled,
  };
};

export type ToggleBundleFreeTrialPayload = {
  bundleId: number;
  type: keyof BundleFreeTrial;
};

export type ToggleSubscriptionBundlePayload =
  | {
      bundleId: number;
      itemId: number;
      type: BundleItemType.Program | BundleItemType.Feature;
    }
  | {
      bundleId: number;
      contentId: string;
      type: BundleItemType.Content;
    };

export const mapToggledContent = (
  bundles: SubscriptionBundle[],
  payload: ToggleSubscriptionBundlePayload,
) => {
  const { bundleId, type } = payload;
  return bundles.map((b) => {
    if (b.id !== bundleId) return b;
    switch (type) {
      case BundleItemType.Feature:
      case BundleItemType.Program: {
        const hasItem = b.items.some((i) => i.itemId === payload.itemId);
        const mappedItems = hasItem
          ? b.items.filter((i) => i.itemId !== payload.itemId)
          : [...b.items, { itemId: payload.itemId, itemType: type }];
        return {
          ...b,
          items: mappedItems,
        };
      }
      case BundleItemType.Content: {
        const hasItem = b.items.some((i) => i.contentId === payload.contentId);
        const mappedItems = hasItem
          ? b.items.filter((i) => i.contentId !== payload.contentId)
          : [...b.items, { contentId: payload.contentId, itemType: type }];
        return {
          ...b,
          items: mappedItems,
        };
      }
    }
  });
};

export const mapToggledFreeTrial = (
  bundles: SubscriptionBundle[],
  payload: ToggleBundleFreeTrialPayload,
) => {
  return bundles.map((b) => {
    if (b.id !== payload.bundleId) return b;

    return {
      ...b,
      freeTrial: {
        ...b.freeTrial,
        [payload.type]: !b.freeTrial[payload.type],
      },
    };
  });
};

export const mapBundlesToInput = (
  bundles: SubscriptionBundle[],
): Array<UpdateBundleInput> =>
  bundles.map((b) => ({
    id: b.id,
    items: b.items.map((i) => {
      if (i.itemType === BundleItemType.Content) {
        return {
          contentId: i.contentId,
          itemType: i.itemType,
        };
      }
      return {
        itemId: i.itemId,
        itemType: i.itemType,
      };
    }),
    freeTrial: b.isPlatformBundle ? null : b.freeTrial,
  }));

export const validatePlatformBundleSubset = (bundles: SubscriptionBundle[]) => {
  const platformBundleUpdate = bundles.find(
    (bundle) => bundle.isPlatformBundle,
  );
  const exclusiveBundleUpdate = bundles.find(
    (bundle) => !bundle.isPlatformBundle,
  );

  const isFeaturesSubset = platformBundleUpdate?.items
    .filter((i) => i.itemType === BundleItemType.Feature)
    .every((item) =>
      exclusiveBundleUpdate?.items
        .filter((i) => i.itemType === BundleItemType.Feature)
        .some((exclusiveItem) => exclusiveItem.itemId === item.itemId),
    );

  const isContentSubset = platformBundleUpdate?.items
    .filter((i) => i.itemType === BundleItemType.Content)
    .every((item) =>
      exclusiveBundleUpdate?.items
        .filter((i) => i.itemType === BundleItemType.Content)
        .some((exclusiveItem) => exclusiveItem.contentId === item.contentId),
    );

  return !isFeaturesSubset || !isContentSubset;
};
